import { FC, ReactNode } from 'react'

type IAcaoLinktree = {
  /** Ícone SVG inline (sem Font Awesome). */
  icon: ReactNode
  titulo: string
  /** Microcopy curto de orientação por perfil (pt-BR). */
  microcopy?: string
  /** Texto de badge opcional, ex.: "Exclusivo para farmacêutico". */
  badge?: string
  /** Estado de carregamento individual da ação. */
  loading?: boolean
  disabled?: boolean
  /** Quando informado, renderiza um link (abre em nova aba se target='_blank'). */
  href?: string
  target?: '_blank' | '_self'
  onClick?: () => void
}

/**
 * Item de ação no estilo "Linktree" do Hub público da receita.
 * Apresentacional: ícone + título + microcopy + chevron, todas com o mesmo
 * peso visual (flat). Usa os tokens de marca Lauduz (sem azul Metronic).
 */
export const AcaoLinktree: FC<IAcaoLinktree> = ({
  icon,
  titulo,
  microcopy,
  badge,
  loading = false,
  disabled = false,
  href,
  target = '_self',
  onClick,
}) => {
  const Chevron = loading ? (
    <span
      className='spinner-border spinner-border-sm acao-linktree__spinner'
      role='status'
      aria-hidden='true'
    />
  ) : (
    <svg
      className='acao-linktree__chevron'
      width='18'
      height='18'
      viewBox='0 0 16 16'
      fill='none'
      aria-hidden='true'
    >
      <path
        d='M6 4l4 4-4 4'
        stroke='currentColor'
        strokeWidth='1.8'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )

  const conteudo = (
    <>
      <span className='acao-linktree__icon' aria-hidden='true'>
        {icon}
      </span>
      <span className='acao-linktree__body'>
        <span className='acao-linktree__title-row'>
          <span className='acao-linktree__title'>{titulo}</span>
          {badge && <span className='acao-linktree__badge'>{badge}</span>}
        </span>
        {microcopy && <span className='acao-linktree__microcopy'>{microcopy}</span>}
      </span>
      {Chevron}
    </>
  )

  const className = `acao-linktree${disabled || loading ? ' acao-linktree--disabled' : ''}`

  if (href) {
    return (
      <a
        className={className}
        href={href}
        target={target}
        rel={target === '_blank' ? 'noopener noreferrer' : undefined}
        aria-busy={loading || undefined}
      >
        {conteudo}
      </a>
    )
  }

  return (
    <button
      type='button'
      className={className}
      onClick={onClick}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
    >
      {conteudo}
    </button>
  )
}
